import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  Search, 
  Star, 
  MapPin, 
  Phone,
  Heart,
  Loader2,
  Utensils,
  Car,
  Scissors,
  Wrench,
  Home,
  Briefcase,
  TreeDeciduous,
  Coffee,
  ShoppingBag,
  Stethoscope,
  GraduationCap,
  Dumbbell,
  PawPrint,
  Landmark,
  Store,
  Building2,
  Clock
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icon mapping
const categoryIcons = {
  restaurant: Utensils,
  food: Utensils,
  cafe: Coffee,
  coffee: Coffee,
  car_repair: Car,
  car_dealer: Car,
  car_wash: Car,
  beauty_salon: Scissors,
  hair_care: Scissors,
  spa: Heart,
  plumber: Wrench,
  electrician: Wrench,
  contractor: Wrench,
  real_estate_agency: Home,
  real_estate: Home,
  lawyer: Briefcase,
  accounting: Briefcase,
  insurance_agency: Briefcase,
  doctor: Stethoscope,
  dentist: Stethoscope,
  hospital: Stethoscope,
  pharmacy: Stethoscope,
  veterinary_care: PawPrint,
  pet_store: PawPrint,
  gym: Dumbbell,
  fitness: Dumbbell,
  school: GraduationCap,
  university: GraduationCap,
  bank: Landmark,
  landscaping: TreeDeciduous,
  garden: TreeDeciduous,
  store: Store,
  shopping: ShoppingBag,
  default: Building2
};

const formatCategory = (category) => {
  if (!category) return 'Business';
  return category
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
};

// Format address with proper capitalization
const formatAddress = (address) => {
  if (!address) return '';
  
  // First, handle UK postcodes - they should be fully uppercase
  // UK postcode pattern: 1-2 letters, 1-2 digits, optional space, digit, 2 letters
  let result = address.replace(
    /\b([A-Za-z]{1,2}[0-9][0-9A-Za-z]?\s*[0-9][A-Za-z]{2})\b/gi,
    (match) => match.toUpperCase()
  );
  
  // Then capitalize each word (but not postcodes since they're already uppercase)
  result = result
    .split(' ')
    .map(word => {
      // If it's already all uppercase (like a postcode part), keep it
      if (word === word.toUpperCase() && word.length <= 4) {
        return word;
      }
      // Capitalize first letter of each word
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  
  // Fix common abbreviations
  return result
    .replace(/\bUk\b/g, 'UK')
    .replace(/\bNw(\d)/gi, 'NW$1')
    .replace(/\bSw(\d)/gi, 'SW$1')
    .replace(/\bSe(\d)/gi, 'SE$1')
    .replace(/\bNe(\d)/gi, 'NE$1')
    .replace(/\bEc(\d)/gi, 'EC$1')
    .replace(/\bWc(\d)/gi, 'WC$1');
};

const getCategoryIcon = (category) => {
  if (!category) return categoryIcons.default;
  const lowerCategory = category.toLowerCase();
  for (const [key, icon] of Object.entries(categoryIcons)) {
    if (lowerCategory.includes(key)) {
      return icon;
    }
  }
  return categoryIcons.default;
};

const FindBusinesses = () => {
  const { token } = useAuth();
  const [postcode, setPostcode] = useState(() => {
    // Restore postcode from sessionStorage
    return sessionStorage.getItem('findBusinesses_postcode') || '';
  });
  const [searching, setSearching] = useState(false);
  const [businesses, setBusinesses] = useState(() => {
    // Restore businesses from sessionStorage
    const saved = sessionStorage.getItem('findBusinesses_results');
    return saved ? JSON.parse(saved) : [];
  });
  const [lastSearchPostcode, setLastSearchPostcode] = useState(() => {
    return sessionStorage.getItem('findBusinesses_lastSearch') || '';
  });
  const [favourites, setFavourites] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [snoozed, setSnoozed] = useState([]);
  const [addingFavourite, setAddingFavourite] = useState(null);
  const [snoozing, setSnoozing] = useState(null);
  const [usedCache, setUsedCache] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchFavourites();
    fetchWebsites();
    fetchSnoozed();
    
    // Re-filter existing results with latest data
    if (businesses.length > 0) {
      refilterExistingResults();
    }
    
    // Check for prefilled postcode from Postcodes page
    const prefillPostcode = sessionStorage.getItem('prefillPostcode');
    if (prefillPostcode) {
      sessionStorage.removeItem('prefillPostcode');
      setPostcode(prefillPostcode);
      // Trigger search after a small delay to let state settle
      setTimeout(() => {
        handleSearchWithPostcode(prefillPostcode);
      }, 100);
    }
  }, []);

  // Save state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem('findBusinesses_postcode', postcode);
  }, [postcode]);

  useEffect(() => {
    sessionStorage.setItem('findBusinesses_results', JSON.stringify(businesses));
  }, [businesses]);

  useEffect(() => {
    sessionStorage.setItem('findBusinesses_lastSearch', lastSearchPostcode);
  }, [lastSearchPostcode]);

  const refilterExistingResults = async () => {
    // Fetch latest lists and re-filter the existing results
    const [favList, webList, snoozedList] = await Promise.all([
      fetchFavourites(),
      fetchWebsites(),
      fetchSnoozed()
    ]);
    
    const favIds = new Set(favList.map(f => f.business_id));
    const webIds = new Set(webList.map(w => w.business_id));
    const snoozedIds = new Set(snoozedList.map(s => s.business_id));
    
    setBusinesses(prev => prev.filter(business => 
      !favIds.has(business.id) && 
      !webIds.has(business.id) && 
      !snoozedIds.has(business.id)
    ));
  };

  const fetchFavourites = async () => {
    try {
      const response = await axios.get(`${API}/favourites`, { headers });
      setFavourites(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch favourites:', error);
      return [];
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${API}/websites`, { headers });
      setWebsites(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch websites:', error);
      return [];
    }
  };

  const fetchSnoozed = async () => {
    try {
      const response = await axios.get(`${API}/snoozed-businesses`, { headers });
      setSnoozed(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch snoozed:', error);
      return [];
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!postcode.trim()) return;
    await handleSearchWithPostcode(postcode);
  };

  const handleSearchWithPostcode = async (searchPostcode) => {
    if (!searchPostcode.trim()) return;

    setSearching(true);
    setBusinesses([]);
    setUsedCache(false);

    try {
      // Re-fetch all lists before searching to ensure we have latest data
      const [favList, webList, snoozedList] = await Promise.all([
        fetchFavourites(), 
        fetchWebsites(), 
        fetchSnoozed()
      ]);

      const response = await axios.post(
        `${API}/businesses/search`,
        { postcode: searchPostcode.trim().toUpperCase() },
        { headers }
      );
      
      // Pre-filter results using the just-fetched data to ensure consistency
      const favIds = new Set(favList.map(f => f.business_id));
      const webIds = new Set(webList.map(w => w.business_id));
      const snoozedIds = new Set(snoozedList.map(s => s.business_id));
      
      const freshResults = response.data.filter(business => 
        !favIds.has(business.id) && 
        !webIds.has(business.id) && 
        !snoozedIds.has(business.id)
      );
      
      setBusinesses(freshResults);
      setLastSearchPostcode(searchPostcode.trim().toUpperCase());
      
      // Check if results were cached (same postcode as last time = likely cached)
      const wasCached = response.headers['x-cache-hit'] === 'true';
      setUsedCache(wasCached);
      
      if (freshResults.length === 0) {
        if (response.data.length > 0) {
          toast.info(`All ${response.data.length} businesses are already in your lists`);
        } else {
          toast.info('No businesses found in this area');
        }
      } else {
        const cacheMsg = wasCached ? ' (from cache)' : '';
        toast.success(`Found ${freshResults.length} new businesses${cacheMsg}`);
      }
    } catch (error) {
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleAddFavourite = async (business) => {
    setAddingFavourite(business.id);
    try {
      await axios.post(`${API}/favourites`, business, { headers });
      toast.success(`${business.name} added to favourites!`);
      // Immediately update local state
      setFavourites(prev => [...prev, { business_id: business.id }]);
      // Also remove from current results immediately
      setBusinesses(prev => prev.filter(b => b.id !== business.id));
    } catch (error) {
      toast.error('Failed to add to favourites');
    } finally {
      setAddingFavourite(null);
    }
  };

  const handleSnooze = async (business) => {
    setSnoozing(business.id);
    try {
      await axios.post(`${API}/snoozed-businesses`, {
        business_id: business.id,
        business_name: business.name
      }, { headers });
      toast.success(`${business.name} snoozed for 1 month`);
      // Immediately update local state
      setSnoozed(prev => [...prev, { business_id: business.id }]);
      // Also remove from current results immediately
      setBusinesses(prev => prev.filter(b => b.id !== business.id));
    } catch (error) {
      toast.error('Failed to snooze business');
    } finally {
      setSnoozing(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="find-businesses">
      <Card className="bg-card/50 border-white/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-primary" />
            Find Businesses
          </CardTitle>
          <CardDescription>
            Search for local businesses by postcode. We&apos;ll find businesses with 10+ reviews, 3.5+ rating, and no website.
            {lastSearchPostcode && (
              <span className="block mt-1 text-xs text-primary">
                Results are cached - searching the same postcode won&apos;t use extra API calls.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Enter UK postcode (e.g., SW1A 1AA)"
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                data-testid="postcode-input"
                className="h-12"
              />
            </div>
            <Button type="submit" disabled={searching} className="h-12 px-6" data-testid="search-btn">
              {searching ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Search Results */}
      {businesses.length > 0 && (
        <Card className="bg-card/50 border-white/5">
          <CardHeader>
            <CardTitle>
              Found {businesses.length} businesses
              {lastSearchPostcode && (
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  in {lastSearchPostcode}
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Businesses with 10+ reviews, 3.5+ rating, and no existing website
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              {businesses.map((business) => {
                const CategoryIcon = getCategoryIcon(business.category);
                
                return (
                  <div
                    key={business.id}
                    className="bg-background/50 border border-white/5 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4 transition-all hover:border-white/10"
                    data-testid={`business-${business.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <CategoryIcon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg">{business.name}</h4>
                          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                            <MapPin className="w-3 h-3" /> {formatAddress(business.address)}
                          </p>
                          {business.phone && (
                            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Phone className="w-3 h-3" /> {business.phone}
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                              <span className="font-medium">{business.rating}</span>
                              <span className="text-muted-foreground">({business.review_count} reviews)</span>
                            </span>
                            <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                              {formatCategory(business.category)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleAddFavourite(business)}
                        disabled={addingFavourite === business.id}
                        className="gap-2"
                        data-testid={`favourite-${business.id}`}
                      >
                        {addingFavourite === business.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Heart className="w-4 h-4" />
                            Add to Favourites
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleSnooze(business)}
                        disabled={snoozing === business.id}
                        variant="outline"
                        className="gap-2"
                        data-testid={`snooze-${business.id}`}
                      >
                        {snoozing === business.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Clock className="w-4 h-4" />
                            Not Yet
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!searching && businesses.length === 0 && (
        <Card className="bg-card/50 border-white/5">
          <CardContent className="py-16 text-center">
            <Search className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">Search for businesses</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Enter a UK postcode above to find local businesses that could benefit from a professional website.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FindBusinesses;
